import { useState } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'

import useContacts from '@/hooks/useContacts'
import useSession from '@/hooks/useSession'
import useOnboarding from '@/hooks/useOnboarding'
import usePhoto from '@/hooks/usePhoto'
import useStudents, { type StudentData } from '@/hooks/useStudents'
import type { Contact } from '@/services/contribution/contribution'
import type { CreateContactArgs } from '@/services/contribution/api'
import { createContactForParticipation } from '@/services/contribution/api'
import { InvalidSessionError } from '@/util/errors'
import { formatAddStudentError } from '@/util/formatters'

import AddOrEditStudentForm from '@components/AddOrEditStudentForm'
import {
  sendStudentAddedEvent,
  sendStudentAddErrorEvent,
  sendContactAddedEvent,
  sendContactAddErrorEvent,
} from '@/services/analytics/gtm'

import Button from '@components/Button'
import PhotoUpload from '@components/PhotoUpload'
import Section from '@components/Section'

import StudentList from './StudentList'
import StudentConfirmationModal from './StudentConfirmationModal'
import InvitationList from './InvitationList'
import AddContactForm from './AddContactForm'
import Step from '@components/WizardStep'
import PrizeProgressBar from './ProgressBars/PrizeProgressBar'

export default function OnboardingWizard() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { session } = useSession()
  const { onboardingStep, completeOnboarding, changeOnboardingStep } = useOnboarding()
  const { t } = useTranslation()
  usePhoto()
  const { students, createStudent } = useStudents()
  const { contacts } = useContacts()
  const [step, setStep] = useState<number>(onboardingStep || 1)
  const [addingStudent, setAddingStudent] = useState(false)
  const [showConfirmStudentsModal, setShowConfirmStudentsModal] = useState(false)

  const nextButtonLabel = t('content.components.wizard.next')
  const previousButtonLabel = t('content.components.wizard.previous')
  const finishButtonLabel = t('content.components.wizard.finish')

  const instructions = [
    {
      title: t('content.components.wizard.step_one_message'),
      step: 1,
    },
    {
      title: t('content.components.wizard.step_two_message'),
      step: 2,
    },

    {
      title: t('content.components.wizard.step_three_message'),
      step: 3,
    },
  ]

  function changeStep(step: number) {
    Sentry.logger.debug(Sentry.logger.fmt`Wizard changes to step ${step}`)
    setStep(step)
    changeOnboardingStep(step)
  }

  function handleFinish() {
    completeOnboarding()
    navigate(`../progress`, { replace: true })
  }

  async function handleAddStudent(data: StudentData) {
    try {
      setAddingStudent(true)
      await createStudent({
        student: {
          firstName: data.firstName,
          lastName: data.lastName,
          classroomId: data.classroomId,
        },
      })
      sendStudentAddedEvent({
        isOnboarding: true,
        studentCount: (students?.length ?? 0) + 1,
      })
      toast.success('Student added successfully!')
      return true
    } catch (error) {
      sendStudentAddErrorEvent({ isOnboarding: true })
      Sentry.logger.error('Error creating student', { error: String(error) })
      const rawErrorMessage =
        error instanceof Error ? error.message : t('content.components.add_or_edit_student.generic_error_toast')
      const formattedMessage = formatAddStudentError(rawErrorMessage, t)
      toast.error(formattedMessage, {
        autoClose: false,
      })
      return false
    } finally {
      setAddingStudent(false)
    }
  }

  const inviteContact = useMutation({
    mutationFn: (contactData: CreateContactArgs) => createContactForParticipation(contactData),
    onSuccess: (data) => {
      if (data.errors && data.errors.length > 0) {
        data.errors.forEach((error) => {
          const errorMessages = error.errors || []
          errorMessages.forEach((errorMessage) => {
            Sentry.logger.error('Error creating contact', { error: errorMessage })
            const formattedMessage = 'An error occurred while inviting this contact. Please try again.'
            toast.error(formattedMessage, {
              autoClose: false,
            })
          })
        })
      } else {
        toast.success('Invitation sent successfully!')
      }
      if (data.contacts) {
        queryClient.setQueryData(['contacts'], () => {
          return data
        })
      }
      queryClient.invalidateQueries({ queryKey: ['contacts'] })
    },
    onError: (error) => {
      const errorMessage = error instanceof Error ? error.message : String(error)
      Sentry.logger.error('Error creating contact', { error: errorMessage })
      const formattedMessage = 'An error occurred while inviting this contact. Please try again.'
      toast.error(formattedMessage, {
        autoClose: false,
      })
    },
  })

  async function handleInviteContact(contact: Partial<Contact>) {
    if (!session || !session.participationId) {
      throw new InvalidSessionError('No session available')
    }

    const result = await inviteContact.mutateAsync({
      name: contact.name || '',
      email: contact.email || '',
      alias: contact.alias || '',
      phone: contact.phone || '',
      language: contact.language || 'en',
      participationId: session?.participationId,
    })
    if (result.errors && result.errors.length > 0) {
      sendContactAddErrorEvent({ isOnboarding: true })
      return false
    }
    sendContactAddedEvent({
      isOnboarding: true,
      hasEmail: (contact.email || '').trim().length > 0,
      hasPhone: (contact.phone || '').trim().length > 0,
      contactCount: (contacts?.length ?? 0) + 1,
    })
    return true
  }

  const AddStudentStep = () => (
    <>
      <title>Onboarding - Add Students - Book Blast by Books Are Fun</title>
      <div className='flex flex-col'>
        <StudentList />

        <div className='flex flex-col pb-8 pt-8 mt-4 border-t border-gray-100'>
          <h2 className='text-lg font-bold text-neutral-800 mb-2 text-center font-serif'>Add A Student</h2>
          <AddOrEditStudentForm onSubmit={handleAddStudent} />
        </div>

        <div className='flex flex-row justify-end w-full mt-4'>
          <Button
            variant='success'
            label={nextButtonLabel}
            size='lg'
            onClick={() => setShowConfirmStudentsModal(true)}
            disabled={addingStudent || !students || students?.length <= 0}
          />
        </div>

        <StudentConfirmationModal
          show={showConfirmStudentsModal}
          students={students ?? []}
          onAddAnother={() => setShowConfirmStudentsModal(false)}
          onContinue={() => {
            setShowConfirmStudentsModal(false)
            changeStep(2)
          }}
        />
      </div>
    </>
  )

  const UploadPhotoStep = () => (
    <>
      <title>Onboarding - Upload Photo - Book Blast by Books Are Fun</title>
      <div className='flex flex-col'>
        <PhotoUpload />
        <div className='flex flex-row justify-between w-full mt-12 mb-2'>
          <Button variant='alt' label={previousButtonLabel} size='lg' onClick={() => changeStep(1)} />
          <Button variant='success' label={nextButtonLabel} size='lg' onClick={() => changeStep(3)} />
        </div>
      </div>
    </>
  )

  const InviteContactsStep = () => (
    <>
      <title>Onboarding - Invite Contacts - Book Blast by Books Are Fun</title>
      <div className='flex flex-col'>
        <div className='px-4 pb-8 lg:w-full'>
          <PrizeProgressBar />
        </div>
        <div className='font-serif font-semibold text-2xl text-gray-700 text-center'>
          {t('content.components.input_contact.title')}
        </div>
        <AddContactForm onSubmit={handleInviteContact} buttonVariant='success' />
        <InvitationList />

        <div className='flex flex-row justify-between w-full mt-4'>
          <Button variant='alt' label={previousButtonLabel} size='lg' onClick={() => changeStep(2)} />
          <Button
            label={finishButtonLabel}
            variant='success'
            size='lg'
            onClick={handleFinish}
            disabled={!contacts || contacts.length <= 1} // contacts[] includes the parent, so we need at least 2 contacts
          />
        </div>
        <div className={`text-xs opacity-80 text-secondary-txt pt-8`}>
          {t('content.components.input_contact.sms_consent_disclaimer')}
        </div>
      </div>
    </>
  )

  const components = [AddStudentStep, UploadPhotoStep, InviteContactsStep]

  return (
    <div className='bg-cta flex flex-col px-2 pb-2'>
      <div className='bg-cta text-center px-4 md:px-8 w-full my-6'>
        <h1 className='text-white text-3xl font-serif text-center pb-4'>
          {t('content.registration_page.title', {
            school: session?.schoolName || `your student's school`,
          })}
        </h1>
        <div className='text-gray-200 pt-2 lg:w-4/5 mx-auto'>{t('content.registration_page.subtitle')}</div>
      </div>
      <div className='text-highlight text-center text-xl font-semibold mb-8'>
        {t('content.registration_page.prompt')}
      </div>
      <h3 className='w-full text-white text-center text-xl font-serif font-semibold'>{instructions[step - 1].title}</h3>
      <div className='flex flex-row justify-center space-x-4 py-2 pb-2 mb-2'>
        <Step value={1} current={step} />
        <Step value={2} current={step} />
        <Step value={3} current={step} />
      </div>

      <Section wrapperStyle='bg-white shadow-sm w-full max-w-4xl mx-auto py-4' rounded={true}>
        {components[step - 1]()}
      </Section>
    </div>
  )
}
